import { useMutation, useQuery } from "src/Hooks/useReactQuery";
import React, { useEffect, useState } from "react";

import APP_ROUTES from "src/routes/routes";
import { createSimulator6DOF, getSimulatorOptions } from "src/services/simulator-gnc";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { alertState, dataJson, uploadingJsonFile } from "src/recoil/atoms/global";
import { useTranslation } from "react-i18next";
import { ROLES, SIMULATION_TYPE, RESOURCE_TYPE } from "src/constant/common";
import { mapFormDataToDefaultData6DOF, parseStringToNumberArray, prepareData6DOF, transformDataVersionsToOptions } from "src/utils/common";
import FormGNCSimulator6DOF from "src/components/GNCSimulator6DOF/form";
import { isEmpty, merge } from "lodash";
import { sim6DOFDefaultData } from "src/components/GNCSimulator6DOF/form/defaultData";
import routeGuard from "src/HOC/routeGuard";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";

const NewGNCSimulator6DOF = () => {
  const { t } = useTranslation("gnc-simulator");
  const navigate = usePatchedNavigate();
  const showAlert = useSetRecoilState(alertState);
  const [jsonValue, setJsonValue] = useRecoilState(dataJson);
  const isUploadingJsonFile = useRecoilValue(uploadingJsonFile);
  const [defaultData, setDefaultData] = useState(sim6DOFDefaultData);

  const createSimulatorMutate = useMutation({
    mutationFn: ({ data }) => createSimulator6DOF({ data }),
  });

  const { data: versions } = useQuery(["getGNCVersions"], () => getSimulatorOptions(SIMULATION_TYPE.GNC_6DOF), {
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isEmpty(jsonValue)) {
      setDefaultData((oldVal) => ({
        ...oldVal,
        input_data: merge({}, sim6DOFDefaultData.input_data, jsonValue),
      }));
    }
  }, [jsonValue]);

  useEffect(() => {
    return () => {
      setJsonValue(null);
    };
  }, []);

  const submitData = ({ data }, callback) => {
    if (data?.type === 1) {
      data.MCS.sigma_I_e = parseStringToNumberArray(data.MCS.sigma_I_e_x, data.MCS.sigma_I_e_y, data.MCS.sigma_I_e_z);
      data.MCS.sigma_I_s = parseStringToNumberArray(data.MCS.sigma_I_s_x, data.MCS.sigma_I_s_y, data.MCS.sigma_I_s_z);
    }

    const dataConvertStringToNumber = mapFormDataToDefaultData6DOF(data, defaultData);
    delete dataConvertStringToNumber?.processAsRoot;

    const keysToDelete = ["sigma_I_e_x", "sigma_I_e_y", "sigma_I_e_z", "sigma_I_s_x", "sigma_I_s_y", "sigma_I_s_z"];

    keysToDelete.forEach((key) => {
      delete dataConvertStringToNumber?.input_data?.MCS?.[key];
    });

    createSimulatorMutate.mutate(
      { data: dataConvertStringToNumber },
      {
        onSuccess: () => {
          showAlert({
            status: true,
            message: t("new.create_success"),
            type: "success",
          });
          navigate(APP_ROUTES.GNC_SIMULATOR_6DOF);
        },
        onError: () => {
          const isSubmitFailed = true;
          callback(isSubmitFailed);
        },
        onSettled: () => {
          const isSubmitFailed = false;
          callback(isSubmitFailed);
        },
      }
    );
  };

  return (
    <FormGNCSimulator6DOF
      submitData={submitData}
      data={prepareData6DOF(defaultData)}
      isUploadingJsonFile={isUploadingJsonFile}
      versions={transformDataVersionsToOptions(versions || [])}
    />
  );
};

export default routeGuard(NewGNCSimulator6DOF, RESOURCE_TYPE.GNC_SIMULATION, ROLES.EDITOR);
